/**
 * AppDropdown — the app's standard dropdown/select, built on Fluent UI v9 Dropdown + Field.
 *
 * Wraps Fluent's `Field` + `Dropdown` + `Option` so the label is semantically
 * associated with the control. Options are passed as a typed array; selection
 * is surfaced via an `onSelect` callback with the raw string value.
 *
 * Styled via Griffel makeStyles + tokens to match the Utah Health brand
 * (consistent with AppInput/AppTextarea and DiscoveryEvalForm field style).
 *
 * No SCSS. No raw HTML selects.
 */

import {
  Dropdown,
  Field,
  Option,
  makeStyles,
  mergeClasses,
  tokens,
  type DropdownProps,
} from '@fluentui/react-components';

export interface AppDropdownOption {
  value: string;
  label: string;
}

export interface AppDropdownProps extends Omit<DropdownProps, 'onOptionSelect' | 'value' | 'selectedOptions' | 'onSelect'> {
  /** Semantic label rendered by Fluent Field (required for a11y). */
  label: string;
  /** Currently selected option value (controls the displayed text). */
  value?: string;
  onSelect?: (val: string) => void;
  options: AppDropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const useStyles = makeStyles({
  dropdown: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    ':focus-within': {
      outlineColor: tokens.colorBrandBackground,
    },
  },
});

export function AppDropdown({
  label,
  value,
  onSelect,
  options,
  placeholder,
  disabled,
  className,
  ...rest
}: AppDropdownProps) {
  const styles = useStyles();

  // Fluent Dropdown uses selectedOptions (array of values) and value (display text).
  const displayLabel = value
    ? (options.find((o) => o.value === value)?.label ?? value)
    : undefined;

  return (
    <Field label={label}>
      <Dropdown
        placeholder={placeholder}
        disabled={disabled}
        selectedOptions={value ? [value] : []}
        value={displayLabel}
        onOptionSelect={(_, d) => onSelect?.(d.optionValue ?? '')}
        className={mergeClasses(styles.dropdown, className)}
        {...rest}
      >
        {options.map((o) => (
          <Option key={o.value} value={o.value}>
            {o.label}
          </Option>
        ))}
      </Dropdown>
    </Field>
  );
}

export default AppDropdown;
